import { useEffect, useCallback } from "react"
import { Bot, User, Users, LifeBuoy, Megaphone, Globe } from 'lucide-react'

import { ConversationList, Conversation } from "@chatscope/chat-ui-kit-react"

import { Channel, ChannelType } from "../../../../shared-src/channel"

import { useChatStore } from "../../state/chat-store"
import { useSessionContext } from '@/state/session-context'
import { useAppVisibility } from '@/state/use-app-visibility'
import { fetchChannels } from '@/state/chat-actions'
import * as env from '@/env'
import { useUserStore } from "@/state/user-store"
import ChannelsHeader from "./channels-header"
import { getColorSet } from '@/theme/color'
import { useOnboardingBannerState } from '@/state/use-onboarding-banner-state'


interface ChannelsSidebarProps {
	isOpen: boolean
	onClose: () => void
}

export function ChannelsSidebar(props: ChannelsSidebarProps) {
	const { session } = useSessionContext()

	const channels = useChatStore(state => state.channels)
	const activeChannelId = useChatStore(state => state.activeChannelId)
	const setActiveChannelId = useChatStore(state => state.setActiveChannelId)
	const unreadChannelIds = useChatStore(state => state.unreadChannelIds)

	const user = useUserStore(state => state.user)

	const handleFetchChannels = useCallback(async () => {
		if (!session?.userId)
			return

		await fetchChannels({ userId: session.userId })
	}, [session?.userId])

	useEffect(() => {
		handleFetchChannels()
	}, [handleFetchChannels])

	useAppVisibility({
		onForeground: handleFetchChannels,
		enabled: !!session?.userId
	})

	const colorSet = getColorSet({ senderColor: user?.color })

	const { isVisible: onboardingBannerVisible } = useOnboardingBannerState()
	const devBannerVisible = !env.isProduction

	let topPadding = ''
	if (devBannerVisible && onboardingBannerVisible)
		topPadding = 'pt-20'
	else if (devBannerVisible)
		topPadding = 'pt-12'
	else if (onboardingBannerVisible)
		topPadding = 'pt-8'

	return (
		<div
			className={`
				fixed md:static inset-y-0 left-0 z-30 bg-white border-r transform transition-transform duration-200 ease-in-out
				w-80 md:w-64
				${props.isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
				${topPadding} md:pt-0
			`}
			style={{
				borderColor: colorSet.border
			}}
		>
			<div className="h-full flex flex-col">
				<ChannelsHeader onClose={props.onClose} />
				<div className="flex-1 overflow-y-auto">
					<ConversationList>
						{channels.map((ch) => {
							const isUnread = unreadChannelIds.has(ch.id) && ch.id !== activeChannelId
							return (
								<Conversation
									key={ch.id}
									name={ch.displayId}
									onClick={() => {
										setActiveChannelId(ch.id)
										props.onClose()
									}}
									active={ch.id === activeChannelId}
									className={isUnread ? "ha-channel-unread" : undefined}
									style={{
										'--conversation-active-bg-color': colorSet.background,
										'--conversation-name-text-color': (ch.id === activeChannelId) ? colorSet.dark : colorSet.text,
										'--conversation-name-font-weight': (ch.id === activeChannelId) ? 'bold' : 'normal',
										'--ha-unread-color': colorSet.border,
									} as React.CSSProperties}
								/>
							)
						})}
					</ConversationList>
				</div>
			</div>
		</div>
	)
} 

export function ChannelIcon(props: { channel: Channel, className?: string }) {
	const icon = () => { switch (props.channel.type) {
		case ChannelType.bot: return <Bot className="w-5 h-5" />
		case ChannelType.dm: return <User className="w-5 h-5" />
		case ChannelType.hangaroundGroup: return <Users className="w-5 h-5" />
		case ChannelType.public: return <Globe className="w-5 h-5" />
		case ChannelType.announcement: return <Megaphone className="w-5 h-5" />
		case ChannelType.support: return <LifeBuoy className="w-5 h-5" />
		default:
			return <Globe className="w-5 h-5" />
	}}

	return <div className={props.className}>
		{icon()}
	</div>
}
